import { useCallback, useMemo } from "react";
import { useInfiniteQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";

export interface PagedResult<T> {
  data: T[];
  hasMore: boolean;
}

/**
 * Offset-paged list on the shared query cache: the first page loads up front,
 * `loadMore` appends the next one. Cached per `queryKey`, so a revisit shows
 * every page already loaded at once, and a refresh/invalidation refetches
 * the loaded pages in the background (the list never blanks out). A new key
 * (e.g. another profile) starts empty — never shows the previous key's rows.
 */
export function useInfiniteList<T, P extends PagedResult<T> = PagedResult<T>>(options: {
  queryKey: QueryKey;
  fetchPage: (offset: number, limit: number) => Promise<P>;
  pageSize: number;
  /** Rows that appear on two pages (the list shifted between fetches) show once. */
  getId: (item: T) => string;
  staleTime?: number;
  errorMessage?: string;
}) {
  const { queryKey, fetchPage, pageSize, getId, staleTime, errorMessage = "Couldn't load. Please try again later." } = options;
  const client = useQueryClient();
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchPage(pageParam, pageSize),
    initialPageParam: 0,
    getNextPageParam: (last, pages) =>
      last.hasMore ? pages.reduce((count, page) => count + page.data.length, 0) : undefined,
    staleTime,
  });

  const items = useMemo(() => {
    const seen = new Set<string>();
    const rows: T[] = [];
    for (const page of query.data?.pages ?? []) {
      for (const item of page.data) {
        const id = getId(item);
        if (seen.has(id)) continue;
        seen.add(id);
        rows.push(item);
      }
    }
    return rows;
  }, [query.data, getId]);

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const keyHash = JSON.stringify(queryKey);
  const refresh = useCallback(
    () => void client.invalidateQueries({ queryKey: JSON.parse(keyHash) as QueryKey, exact: true }),
    [client, keyHash],
  );

  return {
    items,
    /** The first page as returned (for extra fields such as a total count). */
    firstPage: query.data?.pages[0],
    isLoading: query.isPending,
    isLoadingMore: isFetchingNextPage,
    hasMore: Boolean(hasNextPage),
    // A failed "load more" just leaves hasMore as-is (scrolling again retries).
    error: query.isError && !query.isFetchNextPageError ? errorMessage : null,
    loadMore,
    refresh,
  };
}
